import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { registerTenant, checkSubdomain, reset } from '../features/tenant/tenantSlice';
import { login } from '../features/auth/authSlice';
import { toast } from 'react-toastify';

const RegisterBusiness = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, success } = useSelector((state) => state.tenant);

  const [formData, setFormData] = useState({
    businessName: '',
    subdomain: '',
    email: '',
    phone: '',
    address: '',
    ownerName: '',
    ownerEmail: '',
    ownerPassword: '',
    confirmPassword: '',
    plan: 'free',
    serviceConfig: 'full_stack',
    registrationKey: ''
  });

  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);

  const {
    businessName,
    subdomain,
    email,
    phone,
    address,
    ownerName,
    ownerEmail,
    ownerPassword,
    confirmPassword,
    plan,
    serviceConfig,
    registrationKey
  } = formData;

  useEffect(() => {
    if (success) {
      toast.success('Comercio creado exitosamente! Iniciando sesión...');
      // Login automático después del registro
      dispatch(login({ email: ownerEmail, password: ownerPassword }));
      setTimeout(() => {
        navigate('/');
      }, 2000);
    }

    return () => {
      dispatch(reset());
    };
  }, [success, navigate, dispatch, ownerEmail, ownerPassword]);

  const onChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubdomainBlur = async () => {
    if (subdomain.length >= 3) {
      setCheckingSubdomain(true);
      try {
        const result = await dispatch(checkSubdomain(subdomain)).unwrap();
        setSubdomainAvailable(result.available);
      } catch (error) {
        setSubdomainAvailable(false);
      }
      setCheckingSubdomain(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validaciones
    if (!businessName || !subdomain || !email || !ownerName || !ownerEmail || !ownerPassword) {
      toast.error('Por favor complete todos los campos obligatorios');
      return;
    }

    if (!registrationKey || registrationKey.trim() === '') {
      toast.error('🔐 Clave de registro requerida. Contacte al administrador para obtenerla.');
      return;
    }

    if (subdomain.length < 3) {
      toast.error('El subdominio debe tener al menos 3 caracteres');
      return;
    }

    if (!/^[a-z0-9-]+$/.test(subdomain)) {
      toast.error('El subdominio solo puede contener letras minúsculas, números y guiones');
      return;
    }

    if (subdomainAvailable === false) {
      toast.error('El subdominio no está disponible');
      return;
    }

    if (ownerPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (ownerPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    const tenantData = {
      businessName,
      subdomain: subdomain.toLowerCase(),
      email,
      phone,
      address,
      ownerName,
      ownerEmail,
      ownerPassword,
      plan,
      serviceConfig,
      registrationKey
    };

    dispatch(registerTenant(tenantData));
  };

  // Estilos inline
  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  };

  const formCardStyle = {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '40px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
    maxWidth: '900px',
    width: '100%'
  };

  const titleStyle = {
    fontSize: '32px',
    fontWeight: '700',
    color: '#2d3748',
    marginBottom: '10px',
    textAlign: 'center'
  };

  const subtitleStyle = {
    fontSize: '16px',
    color: '#718096',
    marginBottom: '30px',
    textAlign: 'center'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    marginBottom: '20px'
  };

  const formGroupStyle = {
    marginBottom: '20px'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568'
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    transition: 'all 0.3s',
    boxSizing: 'border-box'
  };

  const btnRegisterStyle = {
    width: '100%',
    padding: '14px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: '8px',
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s',
    opacity: loading ? 0.7 : 1
  };

  const linkContainerStyle = {
    marginTop: '20px',
    textAlign: 'center',
    fontSize: '14px',
    color: '#718096'
  };

  const linkStyle = {
    color: '#667eea',
    textDecoration: 'none',
    fontWeight: '600',
    marginLeft: '5px'
  };

  const sectionTitleStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d3748',
    marginBottom: '15px',
    marginTop: '20px',
    borderBottom: '2px solid #e2e8f0',
    paddingBottom: '10px'
  };

  const subdomainFeedbackStyle = {
    fontSize: '12px',
    marginTop: '5px',
    fontWeight: '600'
  };

  return (
    <div style={containerStyle}>
      <div style={formCardStyle}>
        <h1 style={titleStyle}>🏢 Registra tu Comercio</h1>
        <p style={subtitleStyle}>
          Crea tu cuenta SaaS y comienza con 30 días de prueba gratis
        </p>

        <form onSubmit={handleSubmit}>
          {/* Información del Comercio */}
          <h3 style={sectionTitleStyle}>📋 Información del Comercio</h3>

          <div style={gridStyle}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Nombre del Comercio <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                name="businessName"
                value={businessName}
                onChange={onChange}
                placeholder="Ej: Mi Tienda ABC"
                style={inputStyle}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Subdominio <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                name="subdomain"
                value={subdomain}
                onChange={onChange}
                onBlur={handleSubdomainBlur}
                placeholder="mitienda"
                style={{
                  ...inputStyle,
                  borderColor: subdomainAvailable === true ? '#48bb78' : subdomainAvailable === false ? '#f56565' : '#e2e8f0'
                }}
                required
              />
              {checkingSubdomain && (
                <p style={{ ...subdomainFeedbackStyle, color: '#718096' }}>
                  ⏳ Verificando disponibilidad...
                </p>
              )}
              {subdomainAvailable === true && (
                <p style={{ ...subdomainFeedbackStyle, color: '#48bb78' }}>
                  ✅ Subdominio disponible!
                </p>
              )}
              {subdomainAvailable === false && (
                <p style={{ ...subdomainFeedbackStyle, color: '#f56565' }}>
                  ❌ Subdominio no disponible
                </p>
              )}
            </div>
          </div>

          <div style={gridStyle}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Email del Comercio <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={onChange}
                placeholder="contacto@mitienda.com"
                style={inputStyle}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Teléfono</label>
              <input
                type="tel"
                name="phone"
                value={phone}
                onChange={onChange}
                placeholder="+54 11 1234-5678"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Dirección</label>
            <input
              type="text"
              name="address"
              value={address}
              onChange={onChange}
              placeholder="Av. Corrientes 1234, CABA"
              style={inputStyle}
            />
          </div>

          {/* Información del Propietario */}
          <h3 style={sectionTitleStyle}>👤 Información del Propietario</h3>

          <div style={gridStyle}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Nombre Completo <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                name="ownerName"
                value={ownerName}
                onChange={onChange}
                placeholder="Juan Pérez"
                style={inputStyle}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Email <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="email"
                name="ownerEmail"
                value={ownerEmail}
                onChange={onChange}
                placeholder="juan@mitienda.com"
                style={inputStyle}
                required
              />
            </div>
          </div>

          <div style={gridStyle}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Contraseña <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="password"
                name="ownerPassword"
                value={ownerPassword}
                onChange={onChange}
                placeholder="Mínimo 6 caracteres"
                style={inputStyle}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Confirmar Contraseña <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={confirmPassword}
                onChange={onChange}
                placeholder="Repite la contraseña"
                style={inputStyle}
                required
              />
            </div>
          </div>

          {/* Configuración de Servicio */}
          <h3 style={sectionTitleStyle}>⚙️ Configuración del Sistema</h3>
          <div style={formGroupStyle}>
            <label style={labelStyle}>
              Tipo de Solución
            </label>
            <select
              name="serviceConfig"
              value={serviceConfig}
              onChange={onChange}
              style={inputStyle}
            >
              <option value="full_stack">🚀 Todo Completo (POS + Mercado Libre + Tienda)</option>
              <option value="pos_meli">🛒 Punto de Venta + Mercado Libre</option>
              <option value="pos_shop">🏪 Punto de Venta + Tienda Propia</option>
              <option value="pos_only">🖥️ Solo Sistema Punto de Venta</option>
              <option value="ecommerce_meli">☁️ Ecommerce Mercado Libre (Solo Online)</option>
              <option value="ecommerce_shop">🌐 Ecommerce Tienda Propia (Solo Online)</option>
            </select>
          </div>

          {/* Plan */}
          <h3 style={sectionTitleStyle}>💎 Selecciona tu Plan</h3>

          <div style={formGroupStyle}>
            <select
              name="plan"
              value={plan}
              onChange={onChange}
              style={inputStyle}
            >
              <option value="free">🆓 Free - 2 usuarios, 100 productos (Gratis)</option>
              <option value="basic">⭐ Basic - 5 usuarios, 500 productos ($10/mes)</option>
              <option value="pro">🚀 Pro - 15 usuarios, 2000 productos ($50/mes)</option>
              <option value="enterprise">💼 Enterprise - Ilimitado (Consultar)</option>
            </select>
          </div>

          {/* Clave de Registro */}
          <h3 style={sectionTitleStyle}>🔐 Clave de Registro</h3>

          <div style={formGroupStyle}>
            <label style={labelStyle}>
              Clave de Registro <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="password"
              name="registrationKey"
              value={registrationKey}
              onChange={onChange}
              placeholder="Ingrese la clave proporcionada por el administrador"
              style={inputStyle}
              required
            />
            <p style={{ fontSize: '12px', color: '#718096', marginTop: '5px' }}>
              ℹ️ Esta clave es proporcionada por el administrador del sistema para autorizar la creación de nuevos comercios.
            </p>
          </div>

          <button type="submit" style={btnRegisterStyle} disabled={loading}>
            {loading ? '⏳ Registrando...' : '✅ Crear Comercio'}
          </button>
        </form>

        <div style={linkContainerStyle}>
          ¿Ya tienes una cuenta?
          <Link to="/login" style={linkStyle}>
            Iniciar Sesión
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterBusiness;
